import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface SfJobPhoto {
  url: string;
  caption: string | null;
  uploaded_at: string | null;
  is_image: boolean;
}

export interface SfJobConfirmation {
  hasData: boolean;
  jobId: string;
  jobNumber: string | null;
  status: string | null;
  completedAt: string | null;
  signatureUrl: string | null;
  signerName: string | null;
  photos: SfJobPhoto[];
}

interface ApiResponse {
  ok: boolean;
  has_data?: boolean;
  job_id?: string;
  job_number?: string | null;
  status?: string | null;
  completed_at?: string | null;
  signature_url?: string | null;
  signer_name?: string | null;
  photos?: SfJobPhoto[];
  meta?: {
    source?: 'mirror' | string;
    rows_in_db?: number;
    inline_sync_triggered?: boolean;
    photo_count?: number;
    signature_present?: boolean;
    // Legacy fields retained while older deployments are still on the
    // live-call path during rollout.
    endpoint_trace?: Array<{ path: string; ok: boolean; keys?: string[]; error?: string }>;
    raw_attachment_count?: number;
    job_top_level_keys?: string[];
  };
  error?: string;
}

/**
 * Pulls signature + delivery photos from Service Fusion for the given job
 * id. The Netlify function verifies the caller's ownership of an order or
 * invoice referencing that sf_job_id before calling SF.
 *
 * Returns null when the job id is unknown, SF errors out, or SF returned
 * no data (has_data: false). When the function does respond, the request
 * meta is logged to the browser console so we can diagnose the SF
 * endpoint shape without parsing Netlify logs.
 */
export function useSfJobConfirmation(jobId: string | null | undefined) {
  return useQuery<SfJobConfirmation | null>({
    queryKey: ['sf-job-confirmation', jobId ?? null],
    enabled: !!jobId,
    queryFn: async () => {
      if (!jobId) return null;
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) return null;

      const res = await fetch(
        `/.netlify/functions/get-sf-job-confirmation?job_id=${encodeURIComponent(jobId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(
          `[useSfJobConfirmation] job ${jobId}: HTTP ${res.status} ${res.statusText}`,
        );
        return null;
      }
      const body = (await res.json()) as ApiResponse;
      if (body.meta) {
        // eslint-disable-next-line no-console
        console.info(
          `[useSfJobConfirmation] job ${jobId}: photo_count=${body.meta.photo_count ?? 0} sig=${body.meta.signature_present ? '1' : '0'} source=${body.meta.source ?? 'unknown'}`,
          body.meta,
        );
      }
      if (!body.ok || !body.has_data) return null;

      // URLs returned by the function are short-lived Supabase Storage
      // signed URLs — no client-side auth attachment needed; <img src=...>
      // just works.
      return {
        hasData: true,
        jobId: body.job_id ?? jobId,
        jobNumber: body.job_number ?? null,
        status: body.status ?? null,
        completedAt: body.completed_at ?? null,
        signatureUrl: body.signature_url ?? null,
        signerName: body.signer_name ?? null,
        photos: body.photos ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Force a re-pull from Service Fusion for a single job. Used by the "Refresh
 * confirmation" button on /invoices/:id when the auto-sync missed something
 * (most common cause: SF cookies expired between cron runs). Re-fetches the
 * cached query on success so the new signature/photos render immediately.
 */
export function useRefreshSfJobConfirmation() {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; photoCount: number; signaturePresent: boolean; errors: string[] },
    Error,
    string
  >({
    mutationFn: async (jobId: string) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('not signed in');
      const res = await fetch(
        `/.netlify/functions/get-sf-job-confirmation?job_id=${encodeURIComponent(jobId)}&refresh=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const body = (await res.json()) as ApiResponse & {
        meta?: { inline_sync_errors?: string[] };
      };
      return {
        ok: body.ok && !!body.has_data,
        photoCount: body.meta?.photo_count ?? body.photos?.length ?? 0,
        signaturePresent:
          body.meta?.signature_present ?? !!body.signature_url,
        errors: body.meta?.inline_sync_errors ?? [],
      };
    },
    onSuccess: (_data, jobId) => {
      qc.invalidateQueries({ queryKey: ['sf-job-confirmation', jobId] });
    },
  });
}
